import { useRef, useState } from "react";
import { toast } from "react-toastify";
import { FiUpload, FiDownload, FiShield, FiLink, FiExternalLink } from "react-icons/fi";
import Card from "./Card";
import Button from "./Button";
import DataTable from "./DataTable";
import useAsync from "../../hooks/useAsync";
import { listEvidence, uploadEvidence, downloadEvidence, scanEvidence } from "../../services/recordApi";
import { getErrorMessage } from "../../utils/errors";
import { formatDateTime } from "../../utils/format";
import { searchEvidence } from "../../services/evidenceLinkApi";
import EvidenceLinkModal from "../evidence/EvidenceLinkModal";

const SCAN_TONE = { CLEAN: "success", PENDING: "warning", REJECTED: "danger" };

export default function EvidencePanel({ owner = "performance", ownerId, canUpload = false, canScan = false, context = "SELF_ASSESSMENT", title = "Evidence", subtitle, linkItems = [] }) {
  const list = useAsync(() => listEvidence(owner, ownerId), [owner, ownerId], { skip: !ownerId });
  const withLinks = owner === "performance";
  const links = useAsync(() => searchEvidence({ recordId: ownerId, attachmentType: "URL", limit: 200 }), [ownerId], { skip: !ownerId || !withLinks });
  const [linkOpen, setLinkOpen] = useState(false);
  const input = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      await uploadEvidence(owner, ownerId, file, { context });
      toast.success(`${file.name} uploaded. It can be opened once an administrator marks it as checked (clean).`);
      list.reload();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const download = async (row) => {
    try {
      await downloadEvidence(owner, row.id, row.name);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const scan = async (row, status) => {
    try {
      await scanEvidence(owner, row.id, status);
      toast.success(`Marked ${status === "CLEAN" ? "clean" : "rejected"}.`);
      list.reload();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <Card
      title={title}
      subtitle={subtitle || "PDF, PNG, JPEG or text files. A file can be opened once an administrator has checked it."}
      padded={false}
      actions={canUpload && (
        <>
          <input ref={input} type="file" accept=".pdf,.png,.jpg,.jpeg,.txt" hidden onChange={upload} />
          <Button size="sm" variant="secondary" icon={FiUpload} loading={busy} onClick={() => input.current?.click()}>Upload</Button>
          {withLinks && <Button size="sm" variant="ghost" icon={FiLink} onClick={() => setLinkOpen(true)}>Add link</Button>}
        </>
      )}
    >
      <DataTable
        dense
        rows={list.data || []}
        loading={list.loading && !list.data}
        emptyTitle={list.error ? "Evidence not available" : "No files yet"}
        emptyText={list.error || undefined}
        columns={[
          { key: "name", label: "File", render: (r) => <span className="cell-stack"><strong>{r.title || r.name}</strong><small>{r.title ? `${r.name}, ` : ""}{r.context}{r.uploadedAt ? `, ${formatDateTime(r.uploadedAt)}` : ""}</small></span> },
          { key: "scan", label: "Check", render: (r) => <span className={`badge badge-${SCAN_TONE[r.scanStatus] || "neutral"}`} title={r.scanStatus === "PENDING" ? "Waiting for an administrator to check the file. Until then nobody, including the uploader, can open it." : undefined}><span className="badge-dot" />{{ CLEAN: "Checked", PENDING: "Waiting for admin check", REJECTED: "Rejected" }[r.scanStatus] || r.scanStatus}</span> },
          {
            key: "act", label: "", align: "right",
            render: (r) => (
              <span className="row-actions">
                {canScan && r.scanStatus === "PENDING" && (
                  <>
                    <Button size="sm" variant="ghost" icon={FiShield} onClick={() => scan(r, "CLEAN")}>Clean</Button>
                    <Button size="sm" variant="ghost" onClick={() => scan(r, "REJECTED")}>Reject</Button>
                  </>
                )}
                <Button size="sm" variant="ghost" icon={FiDownload} disabled={r.scanStatus !== "CLEAN"} title={r.scanStatus === "CLEAN" ? undefined : "Available after an administrator checks the file"} onClick={() => download(r)} aria-label={`Download ${r.name}`} />
              </span>
            ),
          },
        ]}
      />
      {withLinks && (links.data?.items || []).length > 0 && (
        <DataTable
          dense
          label="Evidence links"
          rowKey="key"
          rows={links.data.items}
          columns={[
            { key: "title", label: "Link", render: (r) => <span className="cell-stack"><strong>{r.title}</strong><small>{r.url}{r.description ? ` — ${r.description}` : ""}</small></span> },
            { key: "for", label: "For", render: (r) => (r.itemTitle ? `${r.itemType}: ${r.itemTitle}` : r.context) },
            { key: "added", label: "Added", render: (r) => formatDateTime(r.addedAt) },
            { key: "act", label: "", align: "right", render: (r) => <Button size="sm" variant="ghost" icon={FiExternalLink} aria-label={`Open ${r.title}`} onClick={() => window.open(r.url, "_blank", "noopener,noreferrer")} /> },
          ]}
        />
      )}
      {withLinks && <EvidenceLinkModal open={linkOpen} recordId={ownerId} context={context} items={context === "SELF_ASSESSMENT" ? linkItems : []} onClose={() => setLinkOpen(false)} onAdded={links.reload} />}
    </Card>
  );
}
